<script>
import { nextTick } from 'vue'
import flatpickr from 'flatpickr'
import { Czech } from "flatpickr/dist/l10n/cs.js"
import { default as English } from "flatpickr/dist/l10n/default.js"
import { Polish } from "flatpickr/dist/l10n/pl.js"

import MessageBoxQuestion from './MessageBoxQuestion.vue'

export default {
    name: "EditForm",

    components: {
        MessageBoxQuestion
    },

    props: {
        userLanguage: {
            type: String,
            default: 'cz'
        },
        title: {
            type: String,
            default: ''
        },
        saveButtonText: {
            type: String,
            default: 'Uložit'
        },
        cancelButtonText: {
            type: String,
            default: 'Zrušit'
        },
        yesButtonText: {
            type: String,
            default: 'Ano'
        },
        noButtonText: {
            type: String,
            default: 'Ne'
        },
        saveText: {
            type: String,
            default: ''
        },
        editData: {
            type: Object,
            default: () => { return { id: -1, type: -1, rows: [] } }
        },
        showQuestion: {
            type: Boolean,
            default: false
        }
    },

    emits: ['form-closed'],

    data() { 
        return {
            showSaveQuestion: false,
            dataToEdit: this.editData
        }     
    },

    computed: {
        dateTimeRows() {
            return this.dataToEdit.rows.filter(r => r.type === 'datetime')
        },
        dateRows() {
            return this.dataToEdit.rows.filter(r => r.type === 'date')
        }
    },

    async mounted() {
        if (this.userLanguage == 'pl') {
            flatpickr.localize(Polish)
        }
        else if (this.userLanguage == 'en') {
            English.firstDayOfWeek = 1
            English.time_24hr = true
            flatpickr.localize(English)
        }
        else {
            flatpickr.localize(Czech)
        }

        for (let i = 0; i < this.dateTimeRows.length; i++) {
            flatpickr('#' + this.dateTimeRows[i].name,
            {
                enableTime: true,
                dateFormat: 'Y-m-dTH:i:S',
                altInput: true,
                altFormat: 'j.n.Y H:i',
                disableMobile: true,
                minuteIncrement: 1
            })
        }

        for (let i = 0; i < this.dateRows.length; i++) {
            flatpickr('#' + this.dateRows[i].name,
            {
                enableTime: false,
                dateFormat: 'Y-m-dTH:i:S',
                altInput: true,
                altFormat: 'j.n.Y',
                disableMobile: true,
                minuteIncrement: 1
            })
        }

        await nextTick()
        document.getElementById('editForm').focus()
    },
    
    methods: {
        // Helpers
        stopEnterPropagation() {
            // dummy to stop click propagation upwards in hierarchy            
        },

        closeForm() {
            this.$emit('form-closed', -1)
        },
        saveForm() {
            let rs = this.dataToEdit.rows.filter(r => r.required)
            let error = false
            for (let i = 0; i < rs.length; i++) {
                if (rs[i].value == null || rs[i].value.length === 0 || (rs[i].type === 'options' && (rs[i].options.find(x => x.key === rs[i].value) == null || rs[i].options.find(x => x.key === rs[i].value).value.length === 0))) {
                    rs[i].error = true
                    error = true
                }
                else {
                    rs[i].error = false
                }
            }
            if (error) {
                return
            }

            if (!this.showQuestion) {
                this.$emit('form-closed', 0, this.dataToEdit)
                return
            }

            this.showSaveQuestion = true
        },
        handleFormKeyDown(event) {
            if (event.key === 'Escape') {
                    this.closeForm()
            }

            if (event.key === 'Enter') {
                    this.saveForm()
            }
        },
        async messageBoxQuestionClosed(result) {
            this.showSaveQuestion = false

            if (result === 0) {
                this.$emit('form-closed', 0, this.dataToEdit)
            }
            else {
                await nextTick()
                document.getElementById('editForm').focus()
                return
            }
        }
    }
}
</script>


<template>
    <div class="edit-form">
        <div id="editForm" @keydown="handleFormKeyDown" tabindex="-1">            
            <span class="title">{{ title }}</span>
            <img src="@/assets/images/x.svg" @click="closeForm()" />
            
            <div class="edit-form-wrapper">
                <div class="edit-form-content">
                    <template v-for="(row, i) in dataToEdit.rows" :key="i">
                        <span class="first-column">{{ row.text }}</span>

                        <span v-if="row.type == 'text'">
                            <input type="text" :class="row.error != null && row.error ? 'error' : ''" v-model="row.value" autocomplete="off" :disabled="row.disabled != null && row.disabled ? true : false" />
                            <span v-if="row.required" class="color-red">&nbsp;*</span>
                        </span>

                        <span v-if="row.type == 'password'">
                            <input type="password" :class="row.error != null && row.error ? 'error' : ''" v-model="row.value" autocomplete="off" :disabled="row.disabled != null && row.disabled ? true : false" onfocus="this.removeAttribute('readonly');" readonly />
                            <span v-if="row.required" class="color-red">&nbsp;*</span>
                        </span>

                        <span v-if="row.type == 'text-multiline'">
                            <textarea :class="row.error != null && row.error ? 'error' : ''" v-model="row.value" @keydown.stop="stopEnterPropagation" :disabled="row.disabled != null && row.disabled ? true : false"></textarea>
                            <span v-if="row.required" class="color-red">&nbsp;*</span>
                        </span>

                        <span v-if="row.type == 'options' || row.type == 'multiple-options'">
                            <select :class="row.error != null && row.error ? 'error' : ''" v-model="row.value" :disabled="row.disabled != null && row.disabled ? true : false" :multiple="row.type == 'multiple-options' ? true : false">
                                <option v-for="o in row.options" :key="o.key" :value="o.key">{{ o.value }}</option>
                            </select>
                            <span v-if="row.required" class="color-red">&nbsp;*</span>
                        </span>

                        <span v-if="row.type == 'datetime'">
                            <input type="text" :class="row.error != null && row.error ? 'error' : ''" :id="row.name" v-model="row.value" autocomplete="off" :disabled="row.disabled != null && row.disabled ? true : false" />
                            <span v-if="row.required" class="color-red">&nbsp;*</span>
                        </span>

                        <span v-if="row.type == 'date'">
                            <input type="text" :class="row.error != null && row.error ? 'error' : ''" :id="row.name" v-model="row.value" autocomplete="off" :disabled="row.disabled != null && row.disabled ? true : false" />
                            <span v-if="row.required" class="color-red">&nbsp;*</span>
                        </span>

                        <span v-if="row.type == 'checkbox'">
                            <input type="checkbox" v-model="row.value" :disabled="row.disabled != null && row.disabled ? true : false" />
                            <span v-if="row.required" class="color-red">&nbsp;*</span>
                        </span>
                    </template>
                </div>

                <div class="edit-form-buttons">
                    <button class="button-small" @click="saveForm">{{ saveButtonText }}</button>
                    <button class="button-small" @click="closeForm">{{ cancelButtonText }}</button>
                </div>
            </div>
        </div>
    </div>

    <MessageBoxQuestion v-if="showSaveQuestion" @form-closed="messageBoxQuestionClosed" :type="1"
        :text="saveText" 
        :yesButtonText="yesButtonText"
        :noButtonText="noButtonText"
    />
</template>